import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppComponentProps } from '../typescriptHelpers';

import type { ColumnsType } from 'antd/lib/table';
import {
    Integration,
    IntegrationInstallationId,
    IntegrationRefreshStatus,
    IntegrationType,
    IntegrationCapability,
} from '../types';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../helpers/integrationHelpers';
import Text from 'antd/lib/typography/Text';
import { dayjs } from '@/extendedDayjs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectAppIntegrations, selectAppTimeFormatSetting } from '../selectors/selectors';
import { abbreviateTimeUnitAndRemoveLeadingSpace, DatetimeFormat, convertTo24HourFormat } from '../timeHelpers';
import {
    iconForIntegrationStatus,
    statusForIntegrationRefreshData,
    statusNodeForIntegrationStatus,
    buildIntegrationTooltipContent,
} from '../helpers/integrationRefreshDataHelpers';
import { antGrey, runwayColors, titleStyle } from './styles/globalStyles';
import { ApiOutlined, CloseOutlined, QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { createAppSettingsIntegrationsRoute } from '../helpers/routeHelpers';
import { displayTextForIntegrationStatus, tagColorForIntegrationStatus } from '../helpers';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../actions/updatable';
import { globalMessage } from '../utils/globalMessage';
import { tryUpdateAppSettings, tryUpdateAppSettingsV2 } from '../actions/appSettings';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Empty from 'antd/lib/empty';
import Divider from 'antd/lib/divider';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const INTEGRATION_REFRESH_TIMEOUT_MS = 600000;

const integrationRefreshButtonTooltip = (integration: Integration) => {
    const refresh = integration.refreshData;

    let header: string | null = null;
    let body: string | null = null;
    let headerColor: string | undefined = undefined;

    if (refresh?.inProgress) {
        const started = dayjs(refresh.refreshStartedAt);
        const now = dayjs();
        const elapsedMs = Math.max(0, now.diff(started));
        const hasTimedOut = elapsedMs > INTEGRATION_REFRESH_TIMEOUT_MS;

        if (!hasTimedOut) {
            header = 'Syncing...';
            headerColor = runwayColors.statusGreen;

            if (refresh.averageDurationMs) {
                const expectedRemainingMs = Math.max(0, refresh.averageDurationMs - elapsedMs);
                if (expectedRemainingMs === 0) {
                    body = `Sync started ${dayjs.duration(elapsedMs, 'milliseconds').humanize()} ago`;
                } else {
                    body = `Expected time remaining: ${dayjs.duration(expectedRemainingMs, 'milliseconds').humanize()}`;
                }
            } else {
                body = `Sync started ${dayjs.duration(elapsedMs, 'milliseconds').humanize()} ago`;
            }
        }
    }

    if (!refresh?.inProgress || !body) {
        if (refresh?.rateLimitHit) {
            header = 'Limit exceeded';
            headerColor = runwayColors.statusYellow;
            if (refresh.rateLimitResetAt) {
                const reset = dayjs(refresh.rateLimitResetAt);
                const now = dayjs();
                const remainingMs = Math.max(0, reset.diff(now));
                const minutes = Math.ceil(remainingMs / 60000);
                body = `Please try again in ${minutes} minutes`;
            } else {
                body = 'Please try again in a few minutes';
            }
        } else if (refresh && refresh.canTriggerManualRefresh === false) {
            body = "You do not have permission to trigger a refresh. Please contact your organization's admin";
        } else {
            body = 'Request sync';
        }
    }

    return { titleNode: buildIntegrationTooltipContent(header, body, headerColor) };
};

const IntegrationRefreshButton = (props: {
    integrationId: IntegrationInstallationId;
    onTriggerRefreshIntegration: () => void;
    disabled?: boolean;
    isSyncing?: boolean;
    onSuccess?: () => void;
}) => {
    const { integrationId, onTriggerRefreshIntegration, disabled, isSyncing, onSuccess } = props;
    const triggerIntegrationRefreshIsLoading = useIsLoading(
        AppSettingsUpdatable.TriggerIntegrationRefresh,
        integrationId,
    );
    const triggerIntegrationRefreshError = useError(AppSettingsUpdatable.TriggerIntegrationRefresh, integrationId);
    const triggerIntegrationRefreshSuccess = useUpdated(AppSettingsUpdatable.TriggerIntegrationRefresh, integrationId);

    React.useEffect(() => {
        if (triggerIntegrationRefreshError) {
            globalMessage.error(triggerIntegrationRefreshError);
        }
    }, [triggerIntegrationRefreshError]);

    React.useEffect(() => {
        if (triggerIntegrationRefreshSuccess) {
            globalMessage.success('Integration refresh kicked off!');
            if (onSuccess) onSuccess();
        }
    }, [triggerIntegrationRefreshSuccess]);

    return (
        <Button
            style={{
                height: 22,
            }}
            type="link"
            loading={triggerIntegrationRefreshIsLoading}
            disabled={disabled}
            icon={<SyncOutlined spin={!!isSyncing} />}
            onClick={onTriggerRefreshIntegration}
        />
    );
};

const IntegrationRefreshPopover = (props: AppComponentProps) => {
    const integrationRefreshIsLoading = useIsLoading(AppSettingsV2Updatable.RefreshIntegrationRefreshData, props.appId);
    const integrationRefreshError = useError(AppSettingsV2Updatable.RefreshIntegrationRefreshData, props.appId);

    const integrations = useSelector((state: RootState) => {
        // always show comms last
        return selectAppIntegrations(state, props.appId).sort((a, b) => {
            if (a.id === IntegrationType.Slack || a.id === IntegrationType.MicrosoftTeams) {
                return 1;
            } else if (b.id === IntegrationType.Slack || b.id === IntegrationType.MicrosoftTeams) {
                return -1;
            }

            return 0;
        });
    });

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));

    React.useEffect(() => {
        if (integrationRefreshError) {
            globalMessage.error(integrationRefreshError);
        }
    }, [integrationRefreshError]);

    React.useEffect(() => {
        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.RefreshIntegrationRefreshData,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                },
                props.appId,
            ),
        );
    }, []);

    const dispatch = useDispatch();

    const triggerRefreshIntegration = (integrationId: IntegrationInstallationId) => {
        dispatch(
            tryUpdateAppSettings(
                AppSettingsUpdatable.TriggerIntegrationRefresh,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    integrationId,
                },
                integrationId,
            ),
        );
    };

    const columns: ColumnsType<Integration> = [
        {
            title: 'Integration',
            render: (integration: Integration) => {
                const integrationStatus = statusForIntegrationRefreshData(integration.integrationError);
                const [icon, alt] = iconAndAltForIntegrationType(integration.id);
                return (
                    <Space align="center">
                        {statusNodeForIntegrationStatus(integrationStatus)}
                        {icon && <img src={icon} alt={alt} width={24} height={24} />}
                        <Text style={{ fontSize: 14 }}>{displayNameForIntegrationType(integration.id)}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Refresh Data',
            align: 'right',
            render: (integration: Integration) => {
                const showRefreshForIntegration =
                    integration.id !== IntegrationType.Slack && integration.id !== IntegrationType.MicrosoftTeams;

                let nextRefreshTooltipString = 'Next refresh time unavailable';
                let lastRefreshPopoverTimeString = '--';
                let integrationStatus = statusForIntegrationRefreshData(integration.integrationError);
                let integrationStatusIcon = iconForIntegrationStatus(integrationStatus);

                let lastRefreshedAt = null;
                let nextRefreshAt = null;

                if (integration.refreshData) {
                    lastRefreshedAt = integration.refreshData.lastRefreshedAt;
                    nextRefreshAt = integration.refreshData.nextRefreshAt;
                }

                if (showRefreshForIntegration) {
                    if (lastRefreshedAt) {
                        const lastRefreshTimeMoment = dayjs(lastRefreshedAt);
                        lastRefreshPopoverTimeString = abbreviateTimeUnitAndRemoveLeadingSpace(
                            lastRefreshTimeMoment.fromNow(),
                        );
                    } else {
                        // show warning icon if no last refresh timestamp
                        integrationStatus = IntegrationRefreshStatus.Warning;
                        integrationStatusIcon = (
                            <QuestionCircleOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
                        );
                    }
                }

                if (nextRefreshAt) {
                    if (dayjs(nextRefreshAt) > dayjs()) {
                        const nextRefreshTimeMoment = dayjs(nextRefreshAt).fromNow();
                        const nextRefreshTooltipTimeString = nextRefreshTimeMoment.replace('in ', '');
                        nextRefreshTooltipString = `Next refresh: ${abbreviateTimeUnitAndRemoveLeadingSpace(
                            nextRefreshTooltipTimeString,
                        ).replace('< ', '')}`;
                        const nextRefreshTimeValues = nextRefreshTooltipString.match(/(\d+)/);
                        if (nextRefreshTimeValues) {
                            nextRefreshTooltipString = nextRefreshTooltipString.replace(
                                nextRefreshTimeValues[0],
                                '< '.concat(nextRefreshTimeValues[0]),
                            );
                        }
                    }
                }

                const refreshData = integration.refreshData;
                const hasTimedOut =
                    refreshData?.inProgress && refreshData.refreshStartedAt ?
                        dayjs().diff(dayjs(refreshData.refreshStartedAt)) > INTEGRATION_REFRESH_TIMEOUT_MS
                    :   false;
                const isSyncing = Boolean(refreshData?.inProgress) && !hasTimedOut;
                const isStabilityMonitoring = integration.capabilities?.includes(
                    IntegrationCapability.StabilityMonitoring,
                );
                const userCanTriggerRefresh = refreshData?.canTriggerManualRefresh ?? false;
                const canShowRefresh = showRefreshForIntegration && !isStabilityMonitoring && userCanTriggerRefresh;
                const isDisabled = Boolean(
                    refreshData &&
                        ((refreshData.inProgress && !hasTimedOut) ||
                            refreshData.rateLimitHit ||
                            refreshData.canTriggerManualRefresh === false),
                );

                const infoTooltip = (() => {
                    const headerText = displayTextForIntegrationStatus(integration);
                    const headerColor = tagColorForIntegrationStatus(integration);
                    const bodyText =
                        lastRefreshedAt ?
                            `Last refresh at ${dayjs(lastRefreshedAt).format(use24HourTimeFormat ? convertTo24HourFormat(DatetimeFormat.ShortMonthDateTime) : DatetimeFormat.ShortMonthDateTime)}`
                        :   'Last refresh time unavailable';
                    return buildIntegrationTooltipContent(headerText, bodyText, headerColor);
                })();

                const timeoutTooltip = buildIntegrationTooltipContent(
                    'Sync timeout',
                    'The sync took longer than expected. Please try again',
                    runwayColors.statusRed,
                );

                return (
                    <Flex justify="flex-end" align="center">
                        {isSyncing ?
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Syncing...
                            </Text>
                        : hasTimedOut ?
                            <Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Sync timeout
                                </Text>
                                <Tooltip color={antGrey.grey9} placement="right" title={timeoutTooltip}>
                                    <CloseOutlined style={{ color: runwayColors.statusRed, fontSize: 12 }} />
                                </Tooltip>
                            </Space>
                        :   <Tooltip color={antGrey.grey9} placement="right" title={infoTooltip}>
                                <Space>
                                    <Text style={{ fontSize: 12 }} ellipsis>
                                        {lastRefreshPopoverTimeString}
                                    </Text>
                                    {integrationStatusIcon}
                                </Space>
                            </Tooltip>
                        }
                        {canShowRefresh ?
                            <>
                                <Divider type="vertical" />
                                <Tooltip
                                    color={antGrey.grey9}
                                    placement="right"
                                    title={integrationRefreshButtonTooltip(integration).titleNode}
                                >
                                    <span style={{ display: 'inline-block' }}>
                                        <IntegrationRefreshButton
                                            integrationId={integration.installationId}
                                            isSyncing={isSyncing}
                                            disabled={isDisabled}
                                            onTriggerRefreshIntegration={() => {
                                                triggerRefreshIntegration(integration.installationId);
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                            </>
                        :   <>
                                <Divider type="vertical" style={{ visibility: 'hidden' }} />
                                <Button
                                    type="link"
                                    style={{ visibility: 'hidden', height: 22 }}
                                    icon={<SyncOutlined />}
                                />
                            </>
                        }
                    </Flex>
                );
            },
        },
    ];

    const appSettingsIntegrationRoute = createAppSettingsIntegrationsRoute(props.orgId, props.appId);

    const empty = (
        <Empty
            image={<ApiOutlined style={{ color: antGrey.grey5, fontSize: 38 }} />}
            imageStyle={{ height: 38 }}
            description={
                <div style={{ color: antGrey.grey7, fontSize: 16 }}>
                    Go to <Link to={{ pathname: appSettingsIntegrationRoute }}>Settings &gt; Integrations</Link>
                    <br />
                    to connect your tools
                </div>
            }
            style={{ padding: 20 }}
        />
    );

    if (integrations.length === 0) {
        return empty;
    } else {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 6,
                        paddingBottom: 6,
                        paddingLeft: 12,
                        paddingRight: 12,
                    }}
                >
                    <Text style={{ ...titleStyle, fontSize: 11 }}>Integrations</Text>
                    <Link to={{ pathname: appSettingsIntegrationRoute }}>View all</Link>
                </div>
                <Divider style={{ width: '100%', padding: 0, margin: 0 }} />
                <Table
                    loading={integrationRefreshIsLoading}
                    showHeader={false}
                    pagination={false}
                    bordered={false}
                    columns={columns}
                    dataSource={integrations}
                    size="small"
                    className="integration-refresh-table"
                />
            </div>
        );
    }
};

export default IntegrationRefreshPopover;
